import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ShieldCheck,
  KeyRound,
  Clock,
  Users,
  EyeOff,
  ServerOff,
  FileX,
  DatabaseZap,
  ArrowRight,
  Fingerprint,
  CheckCircle2,
  XCircle,
  Loader2,
} from 'lucide-react';
import { GovernmentDisclaimer } from '../components/GovernmentDisclaimer';
import { BuildAttestationPanel } from '../components/BuildAttestationPanel';

// ─── Warrant-canary fingerprint (OUT-OF-BAND ANCHOR) ──────────────
// This fingerprint is HARDCODED intentionally. It is the
// ed25519 public key the operator of THIS deployment uses to sign
// the warrant canary. The value here must match what
// `GET /api/canary` returns. If it doesn't, the deployment has been
// replaced, the key has been rotated, or you're looking at a
// spoofed frontend.
//
// DO NOT UPDATE THIS CONSTANT without rotating the canary key
// deliberately — out-of-band publishing is the entire point.
// Users also pin this value in: the project README, the founder's
// personal blog, their GitHub profile, and (future) a Keybase proof.
const CANARY_FINGERPRINT_FULL =
  'afcb5e690756ff163f8469f695b846529ca7784e7b8f2e98b075942e9b01d492';
const CANARY_FINGERPRINT_SHORT = 'afcb 5e69 0756 ff16 3f84 69f6 95b8 4652';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CanaryFingerprintSection = () => {
  const [state, setState] = React.useState({ status: 'idle' });

  const verifyNow = async () => {
    setState({ status: 'checking' });
    try {
      const res = await fetch(`${API}/canary`, { credentials: 'omit' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const liveFull = (json.public_key_hex || '').toLowerCase();
      const match = liveFull === CANARY_FINGERPRINT_FULL.toLowerCase();
      setState({
        status: match ? 'match' : 'mismatch',
        liveFingerprint: json.public_key_fingerprint_short,
        liveFull,
      });
    } catch (e) {
      setState({ status: 'error', error: e.message || 'unreachable' });
    }
  };

  return (
    <section className="max-w-5xl mx-auto px-6 py-12 lg:py-20">
      <div
        className="rounded-3xl border border-emerald-500/20 bg-emerald-500/5 p-8 lg:p-12"
        data-testid="manifesto-canary-fingerprint"
      >
        <div className="flex items-center gap-3 mb-4">
          <Fingerprint className="w-6 h-6 text-emerald-400" strokeWidth={1.75} />
          <h2
            className="text-2xl lg:text-3xl font-bold text-white tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Our warrant-canary fingerprint.
          </h2>
        </div>
        <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-6">
          Every Government Shield deployment publishes a daily-signed statement
          asserting it has not been compelled to hand over user data. That
          statement is only as trustworthy as the <strong>public key</strong>
          {' '}used to sign it. Here is ours, pinned into this page so a replaced
          deployment cannot silently swap it.
        </p>

        <div className="rounded-xl border border-emerald-500/20 bg-black/40 p-5 font-mono">
          <p className="text-[10px] uppercase tracking-widest text-emerald-400/70 mb-2">
            ed25519 public key · short
          </p>
          <p
            className="text-lg text-white tracking-[0.2em] break-all select-all"
            data-testid="manifesto-canary-fingerprint-short"
          >
            {CANARY_FINGERPRINT_SHORT}
          </p>
          <p className="text-[10px] uppercase tracking-widest text-emerald-400/70 mt-4 mb-2">
            ed25519 public key · full (64 hex)
          </p>
          <p
            className="text-[11px] text-slate-400 break-all select-all"
            data-testid="manifesto-canary-fingerprint-full"
          >
            {CANARY_FINGERPRINT_FULL}
          </p>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            onClick={verifyNow}
            disabled={state.status === 'checking'}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="manifesto-canary-verify-btn"
          >
            {state.status === 'checking' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ShieldCheck className="w-4 h-4 mr-2" />
            )}
            Verify live canary now
          </Button>
          {state.status === 'match' && (
            <span
              className="inline-flex items-center gap-2 text-sm text-emerald-300"
              data-testid="manifesto-canary-verify-match"
            >
              <CheckCircle2 className="w-4 h-4" />
              Live deployment matches this fingerprint
            </span>
          )}
          {state.status === 'mismatch' && (
            <span
              className="inline-flex items-center gap-2 text-sm text-red-300"
              data-testid="manifesto-canary-verify-mismatch"
            >
              <XCircle className="w-4 h-4" />
              MISMATCH — live key is <code className="text-[11px]">{state.liveFingerprint}</code>
            </span>
          )}
          {state.status === 'error' && (
            <span className="inline-flex items-center gap-2 text-sm text-amber-300">
              <XCircle className="w-4 h-4" />
              Could not reach canary endpoint: {state.error}
            </span>
          )}
        </div>

        <p className="mt-6 text-xs text-slate-500 leading-relaxed max-w-3xl">
          This same fingerprint is independently published on the project
          README, the founder's personal channels, and (where available) a
          Keybase proof. If three or more out-of-band sources agree and the
          live endpoint matches — the signing key is genuine. If any source
          disagrees, assume a compromise and abort.
        </p>
      </div>
    </section>
  );
};


/**
 * ManifestoPage — public, no-auth explainer for /manifesto.
 *
 * Serves two audiences:
 *   1. Privacy-curious visitors hitting the login wall — gives them a
 *      concrete "why should I trust this" story before they commit to
 *      requesting an invite.
 *   2. Journalists / security researchers who want a pageable URL they
 *      can link to when writing about the project.
 *
 * Layout is intentionally a single scrollable column on mobile, two-column
 * principles grid on desktop. No fancy animations, no video loops, no
 * cookie banners — matches the anti-surveillance posture at the meta
 * level too.
 */

const Principle = ({ icon: Icon, title, summary, detail, children }) => (
  <div
    className="relative p-6 rounded-2xl bg-card/40 backdrop-blur border border-border"
    data-testid={`principle-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mb-5">
      <Icon className="w-6 h-6 text-primary" strokeWidth={1.75} />
    </div>
    <h3
      className="text-xl font-bold text-white tracking-tight mb-2"
      style={{ fontFamily: 'Outfit, sans-serif' }}
    >
      {title}
    </h3>
    <p className="text-slate-300 leading-relaxed mb-4">{summary}</p>
    {detail && (
      <p className="text-sm text-slate-500 leading-relaxed font-mono bg-secondary/20 rounded-lg p-3 border border-border">
        {detail}
      </p>
    )}
    {children}
  </div>
);

const Anti = ({ icon: Icon, label, body }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-5 h-5 text-red-400/80 mt-0.5 shrink-0" strokeWidth={1.75} />
    <div>
      <p className="text-white font-medium text-sm">{label}</p>
      <p className="text-slate-500 text-sm leading-relaxed">{body}</p>
    </div>
  </div>
);

export const ManifestoPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#05050A' }}>
      {/* Top nav */}
      <header className="sticky top-0 z-10 backdrop-blur bg-[#05050A]/80 border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-white hover:text-primary transition-colors"
            data-testid="manifesto-home-link"
          >
            <ShieldCheck className="w-6 h-6 text-primary" />
            <span
              className="font-bold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Government Shield
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-300"
                data-testid="manifesto-signin-btn"
              >
                Sign in
              </Button>
            </Link>
            <Link to="/waitlist">
              <Button size="sm" data-testid="manifesto-waitlist-btn">
                Request access
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 py-20 lg:py-32">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          <span className="text-xs text-emerald-300 uppercase tracking-wider font-medium">
            Invite-only · Peer-reviewed
          </span>
        </div>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05] mb-6"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Private by design.
          <br />
          <span className="text-primary">Sovereign by default.</span>
        </h1>
        <p className="text-lg sm:text-xl text-slate-400 leading-relaxed max-w-2xl">
          Government Shield is an encrypted messenger built for people
          whose adversary isn't advertisers. No archives past 24 hours. Keys
          that never leave your device. Access only through people you
          already trust.
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/waitlist">
            <Button size="lg" data-testid="manifesto-cta-waitlist">
              Request access
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/selfhost">
            <Button variant="outline" size="lg" data-testid="manifesto-cta-selfhost">
              Run your own server
            </Button>
          </Link>
          <a href="#principles">
            <Button variant="ghost" size="lg" data-testid="manifesto-cta-read">
              Read the principles
            </Button>
          </a>
        </div>
      </section>

      {/* Principles */}
      <section
        id="principles"
        className="max-w-5xl mx-auto px-6 py-12 lg:py-20 scroll-mt-20"
      >
        <div className="mb-12">
          <p className="text-xs uppercase tracking-wider text-primary font-semibold mb-3">
            Three principles · No exceptions
          </p>
          <h2
            className="text-3xl lg:text-4xl font-bold text-white tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            What you get. Why it matters.
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <Principle
            icon={Clock}
            title="No archives past 24h"
            summary="Every message is auto-destroyed 24 hours after it's sent. The server has nothing to leak, subpoena, or sell — because it doesn't keep it."
            detail="Background job runs hourly: db.messages.delete_many({expires_at: $lt: now}). Applied uniformly to text, files, reactions, and group chat state."
          />
          <Principle
            icon={KeyRound}
            title="Keys never leave your device"
            summary="Your RSA-OAEP keypair is generated in your browser and stored locally. The server sees encrypted blobs only — it couldn't read your messages if a court told it to."
            detail="Web Crypto API · AES-GCM per-message · RSA-OAEP 2048 for key exchange · Safety numbers let you verify your peer's fingerprint out-of-band."
          />
          <Principle
            icon={Users}
            title="Invite-only, peer-reviewed"
            summary="No public signup. Every member was personally vouched for by someone already in. Waitlist requests are reviewed one-by-one by existing members."
            detail="Single-use invite codes with 48h TTL · Optional request blurb reviewed by members · Approval mints an invite tied to the requester's row (audit trail)."
          />
        </div>
      </section>

      {/* What we do NOT do */}
      <section className="max-w-5xl mx-auto px-6 py-12 lg:py-20">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-wider text-red-400/80 font-semibold mb-3">
            Just as important
          </p>
          <h2
            className="text-3xl lg:text-4xl font-bold text-white tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            What we deliberately don't do.
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 lg:gap-6">
          <Anti
            icon={ServerOff}
            label="No third-party analytics"
            body="No Google Analytics, no Segment, no Mixpanel, no heatmaps. The server literally has no tracking pixels to serve."
          />
          <Anti
            icon={FileX}
            label="No email delivery infrastructure"
            body="We don't send marketing, password reset, or invite emails. Recovery is via an offline key you copy once — not a link in your inbox."
          />
          <Anti
            icon={DatabaseZap}
            label="No metadata harvesting"
            body="We don't log who's online, what you type, who you message, or when. WebSocket traffic is ephemeral; we keep only what's required to route a message right now."
          />
          <Anti
            icon={EyeOff}
            label="No biometric lock-in"
            body="No face ID, no fingerprint, no device-bound secrets that vendors can subpoena. Your password and your offline recovery key — that's it."
          />
        </div>
      </section>

      {/* Sovereignty */}
      <section className="max-w-5xl mx-auto px-6 py-12 lg:py-20">
        <div className="relative rounded-3xl border border-primary/20 bg-primary/5 p-8 lg:p-12 overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-30" />
          <div className="relative">
            <h2
              className="text-3xl lg:text-4xl font-bold text-white tracking-tight mb-5"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Who we're for.
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed max-w-3xl mb-6">
              Journalists working sources in hostile jurisdictions. Lawyers
              talking to clients. Researchers who need to collaborate without
              being surveilled. Families who've been through a stalking
              incident and never want to feel that way again. Organizers,
              founders, whistleblowers.
            </p>
            <p className="text-base text-slate-400 leading-relaxed max-w-3xl">
              If your threat model is a nation-state with unlimited budget,
              no app on Earth (including this one) will fully protect you —
              that requires air-gapped hardware. But for the more common
              reality — corporate surveillance, casual device compromise,
              opportunistic subpoenas, data breaches at Big Tech — we've
              designed to make the cost of going after you exceed whatever
              reward is on the other side.
            </p>
          </div>
        </div>
      </section>

      {/* Warrant-canary OOB fingerprint — hardcoded trust anchor */}
      <CanaryFingerprintSection />

      {/* Build attestation — reproducible builds + Sigstore */}
      <BuildAttestationPanel />

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 py-12 lg:py-20 text-center">
        <h2
          className="text-3xl lg:text-4xl font-bold text-white tracking-tight mb-4"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Ready when you are.
        </h2>
        <p className="text-slate-400 leading-relaxed max-w-xl mx-auto mb-8">
          Request access, or sign in if a member has already shared an
          invite code with you.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/waitlist">
            <Button size="lg" data-testid="manifesto-footer-cta-waitlist">
              Request access
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/login">
            <Button variant="outline" size="lg" data-testid="manifesto-footer-cta-signin">
              I have an invite
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-5xl mx-auto px-6 py-10 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-primary/60" />
              <span>Government Shield</span>
            </div>
            <div className="flex gap-6">
              <Link
                to="/login"
                className="hover:text-slate-300 transition-colors"
                data-testid="manifesto-footer-signin"
              >
                Sign in
              </Link>
              <Link
                to="/waitlist"
                className="hover:text-slate-300 transition-colors"
                data-testid="manifesto-footer-waitlist"
              >
                Request access
              </Link>
            </div>
          </div>
          <GovernmentDisclaimer />
        </div>
      </footer>
    </div>
  );
};

export default ManifestoPage;
